<script lang="ts">
  import { faTrash } from '@fortawesome/free-solid-svg-icons';
  import type { EditorState } from '../editor-state.svelte';
  import Icon from './Icon.svelte';

  const { editor }: { editor: EditorState } = $props();

  /** Why the last rename was refused, until the next one is accepted. */
  let problem = $state<string | null>(null);

  /**
   * Shows the stored value without fighting the person typing: a field that already reads
   * the same number, such as `1.` on its way to `1.5`, is left alone.
   */
  function shows(field: HTMLInputElement, value: number) {
    const sync = (next: number) => {
      if (field.valueAsNumber !== next) field.value = String(next);
    };
    sync(value);
    return { update: sync };
  }

  // Each valid name is taken as it is typed, so autosave sees it; an invalid one says why
  // and goes back to the stored name when the field is left.
  function rename(index: number, field: HTMLInputElement) {
    problem = editor.renameParam(index, field.value.trim());
  }

  function leaveName(index: number, field: HTMLInputElement) {
    if (problem) field.value = editor.params[index]?.name ?? '';
  }

  // A cleared or half-typed number changes nothing; leaving the field shows the value again.
  function enter(index: number, field: HTMLInputElement) {
    if (field.value.trim() !== '' && Number.isFinite(field.valueAsNumber)) {
      editor.setParamValue(index, field.valueAsNumber);
    }
  }
</script>

{#if problem}<p class="problem" role="alert">{problem}</p>{/if}

{#if editor.params.length === 0}
  <p class="empty">Add one, then read it in the script as <code>params.name</code>.</p>
{:else}
  <ul class="params panel-list">
    {#each editor.params as param, index (index)}
      <li class="panel-item">
        <input
          type="text"
          class="name"
          spellcheck="false"
          aria-label="Name of param {index + 1}"
          value={param.name}
          oninput={(event) => rename(index, event.currentTarget)}
          onchange={(event) => leaveName(index, event.currentTarget)}
        />
        <input
          type="number"
          step="any"
          class="value"
          aria-label="Value of {param.name}"
          use:shows={param.value}
          oninput={(event) => enter(index, event.currentTarget)}
          onblur={(event) => (event.currentTarget.value = String(param.value))}
        />
        <input
          type="text"
          class="description"
          placeholder="Description"
          aria-label="Description of {param.name}"
          value={param.description}
          oninput={(event) => editor.describeParam(index, event.currentTarget.value)}
        />
        <button
          type="button"
          class="icon"
          aria-label="Delete {param.name}"
          title="Delete {param.name}"
          onclick={() => editor.removeParam(index)}
        >
          <Icon icon={faTrash} />
        </button>
      </li>
    {/each}
  </ul>
{/if}

<style>
  .empty {
    margin: 0;
    color: var(--color-text-muted);
    font-size: 0.8125rem;
  }

  .params {
    margin: 0;
    flex: 1;
    min-height: 0;
    overflow: auto;
  }

  /* Name, value, description and delete on one line. */
  li {
    display: grid;
    grid-template-columns: minmax(0, 1fr) 5.5rem minmax(0, 2fr) auto;
    gap: 4px;
    align-items: center;
  }

  input {
    box-sizing: border-box;
    width: 100%;
    min-width: 0;
    padding: 3px 6px;
    border: 1px solid var(--color-border);
    border-radius: 6px;
    background: var(--color-bg);
    color: var(--color-text);
    font: inherit;
    font-size: 0.8125rem;
    font-variant-numeric: tabular-nums;
  }

  .name {
    font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
    font-size: 0.75rem;
  }

  button.icon {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    border: 1px solid transparent;
    border-radius: 6px;
    background: none;
    color: var(--color-text-muted);
    font: inherit;
    font-size: 0.75rem;
    cursor: pointer;
  }

  button.icon {
    padding: 4px 6px;
  }

  button.icon:hover {
    border-color: var(--color-border);
    color: var(--color-text);
  }

  .problem {
    margin: 0 0 var(--space-2);
    color: var(--color-danger);
    font-size: 0.75rem;
  }
</style>
